import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, AlertTriangle, CreditCard, Users, Shield, Gavel } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const TermsOfService = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Users,
      title: "Acceptance of Terms",
      content: `By accessing, using, or registering for BrixBot's AI chatbot services and platform ("Service"), you ("User", "Customer", "Client", or "you") agree to be legally bound by these Terms of Service ("Terms") and all applicable laws and regulations. If you do not agree with any provision of these Terms, you are prohibited from using our Service and must discontinue use immediately.

These Terms constitute a legally binding agreement between you and BrixBot ("Company", "we", "us", or "our") and apply to all users of our Service, including but not limited to visitors, registered users, customers, enterprises, and any third parties accessing our platform on your behalf.

By accepting these Terms, you represent and warrant that:
• You are at least 18 years of age and have legal capacity to enter into contracts
• You have read, understood, and agree to be bound by these Terms
• You are authorized to enter into this agreement on behalf of your organization (if applicable)
• All information provided is accurate, current, and complete
• You will comply with all applicable laws and regulations`
    },
    {
      icon: Shield,
      title: "Service Description & Limitations",
      content: `BrixBot provides artificial intelligence-powered chatbot creation, deployment, and management services ("AI Services") including but not limited to:

• Custom AI chatbot development using machine learning algorithms
• Integration with customer-provided data and systems
• Monthly hosting, maintenance, and technical support
• Analytics, performance monitoring, and reporting services
• Customer service and consultation

SERVICE LIMITATIONS AND DISCLAIMERS:

AI TECHNOLOGY LIMITATIONS: Our AI chatbots use machine learning algorithms that may:
• Provide incorrect, incomplete, or outdated information
• Make errors in interpretation or response generation
• Fail to understand complex queries or context
• Generate responses that may be inappropriate or unexpected
• Experience technical failures or downtime

NO WARRANTY OF AI ACCURACY: WE PROVIDE NO WARRANTY, GUARANTEE, OR ASSURANCE REGARDING THE ACCURACY, COMPLETENESS, RELIABILITY, OR APPROPRIATENESS OF AI-GENERATED RESPONSES. AI technology is inherently probabilistic and may produce unpredictable results.

CONTINUOUS LEARNING LIMITATIONS: While our AI systems learn and improve over time, we cannot guarantee consistent performance improvements or error elimination.

SERVICE AVAILABILITY: We strive for high uptime but do not guarantee uninterrupted service availability. Planned and unplanned maintenance may occur.`
    },
    {
      icon: CreditCard,
      title: "Payment Terms & Financial Obligations",
      content: `PAYMENT STRUCTURE:
• Setup Fee: €600 total (€200 down payment required before work begins, €400 due upon delivery)
• Monthly Hosting: Starting at €25/month based on selected plan and usage
• Annual Payment Discount: 10% discount available for annual commitments
• Currency fluctuations may affect pricing for international customers

PAYMENT TERMS & CONDITIONS:
• Down payment is NON-REFUNDABLE and required before any work commences
• Final payment due within 15 days of chatbot delivery and client approval
• Monthly fees are billed in advance on the same date each month
• Automatic payment authorization required for recurring fees
• Late payment fees of 1.5% per month (18% annually) apply to overdue amounts
• Service suspension may occur immediately upon payment default
• Collection costs and legal fees will be charged to defaulting customers

REFUND POLICY:
• Setup fees are NON-REFUNDABLE under all circumstances
• Monthly fees are NON-REFUNDABLE except as required by law
• No refunds for partial month usage or service cancellation
• Refunds, if applicable, processed within 30 business days

PRICE CHANGES:
• We reserve the right to modify pricing with 30 days written notice
• Existing customers locked into current pricing for 12 months
• Price increases limited to 10% annually unless significant platform changes occur`
    },
    {
      icon: FileText,
      title: "User Responsibilities & Data Ownership",
      content: `USER OBLIGATIONS & WARRANTIES:

ACCOUNT MANAGEMENT:
• Maintain strict confidentiality of account credentials
• Immediately notify us of any unauthorized access or security breaches
• Provide accurate, complete, and current information at all times
• Update account information promptly when changes occur

DATA OWNERSHIP & LICENSING:
• You retain ownership of all data, content, and materials provided to us ("User Data")
• You grant us a worldwide, royalty-free license to use User Data solely for providing services
• You warrant that you own or have legal rights to all User Data provided
• You warrant that User Data does not infringe on third-party intellectual property rights

CONTENT WARRANTIES & INDEMNIFICATION:
YOU WARRANT AND REPRESENT THAT:
• All User Data is legally obtained and owned by you
• User Data does not violate any laws, regulations, or third-party rights
• You have all necessary permissions, licenses, and consents for User Data use
• User Data does not contain malicious code, viruses, or harmful content
• You will indemnify and hold us harmless from any claims arising from User Data

PROHIBITED DATA TYPES:
You may not provide or use our Service with:
• Copyrighted material without proper authorization
• Personal data of minors under 13 years of age
• Sensitive personal data without explicit consent
• Illegal, harmful, threatening, or abusive content
• Data that violates privacy laws or regulations
• Proprietary information belonging to third parties without authorization

COMPLIANCE OBLIGATIONS:
• Comply with all applicable data protection laws (GDPR, CCPA, etc.)
• Obtain necessary consents for data processing and AI training
• Implement appropriate data security measures
• Report data breaches to us within 24 hours`
    },
    {
      icon: AlertTriangle,
      title: "Prohibited Uses & Violations",
      content: `STRICTLY PROHIBITED ACTIVITIES:

ILLEGAL ACTIVITIES:
• Any use that violates local, national, or international laws
• Money laundering, fraud, or other financial crimes
• Copyright, trademark, or patent infringement
• Privacy violations or unauthorized data collection
• Hacking, phishing, or social engineering attempts

HARMFUL OR ABUSIVE CONDUCT:
• Harassment, bullying, or threatening behavior
• Hate speech, discrimination, or incitement to violence
• Impersonation of individuals, organizations, or entities
• Distribution of malware, viruses, or malicious code
• Spam, unsolicited communications, or mass messaging

TECHNICAL VIOLATIONS:
• Reverse engineering, decompiling, or attempting to access source code
• Unauthorized access to our systems, networks, or databases
• Interference with service operation or security measures
• Circumvention of usage limits or access controls
• Automated data scraping or content harvesting

COMMERCIAL VIOLATIONS:
• Reselling or redistributing our services without authorization
• Creating competing services using our technology or methodologies
• Using our platform for unauthorized commercial purposes
• Sub-licensing or transferring access rights without consent

CONSEQUENCES OF VIOLATIONS:
Violations may result in:
• Immediate account suspension or termination
• Legal action and prosecution to the full extent of the law
• Liability for damages, costs, and legal fees
• Forfeiture of all fees paid and services provided
• Permanent ban from using our services
• Cooperation with law enforcement authorities`
    },
    {
      icon: Gavel,
      title: "Comprehensive Liability Limitations",
      content: `DISCLAIMER OF WARRANTIES:

OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, INCLUDING BUT NOT LIMITED TO:
• Warranties of merchantability, fitness for particular purpose, or non-infringement
• Warranties of accuracy, reliability, or completeness of AI responses
• Warranties of uninterrupted, secure, or error-free service operation
• Warranties of compatibility with your systems or requirements
• Any warranties arising from course of dealing or usage of trade

AI-SPECIFIC DISCLAIMERS:
• WE DO NOT WARRANT THE ACCURACY, COMPLETENESS, OR RELIABILITY OF AI-GENERATED CONTENT
• AI RESPONSES MAY BE INCORRECT, INAPPROPRIATE, OR HARMFUL
• AI TECHNOLOGY IS EXPERIMENTAL AND MAY PRODUCE UNPREDICTABLE RESULTS
• WE ARE NOT RESPONSIBLE FOR DECISIONS MADE BASED ON AI OUTPUT
• AI SYSTEMS MAY EXHIBIT BIAS, DISCRIMINATION, OR UNFAIR TREATMENT

LIMITATION OF LIABILITY:

TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
• INDIRECT, INCIDENTAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES
• LOSS OF PROFITS, REVENUE, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL  
• BUSINESS INTERRUPTION, SERVICE DOWNTIME, OR SYSTEM FAILURES
• DAMAGES ARISING FROM AI RESPONSES OR CHATBOT INTERACTIONS
• DAMAGES CAUSED BY THIRD-PARTY ACTIONS, CONTENT, OR SERVICES
• DAMAGES EXCEEDING THE TOTAL AMOUNT PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM

MAXIMUM LIABILITY CAP:
Our total liability for all claims arising from or related to these Terms or our Service shall not exceed the lesser of:
• €500 (five hundred euros), or
• The total amount actually paid by you to us in the 12 months immediately preceding the claim

INDEMNIFICATION:
You agree to indemnify, defend, and hold harmless BrixBot, its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising from:
• Your use or misuse of our Service
• Your violation of these Terms or applicable laws
• Your User Data or its use in our Service
• Any third-party claims related to your chatbot or its responses`
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-950 relative overflow-hidden">
      <SEOHead 
        title="Terms of Service - BrixBot AI Legal Agreement" 
        description="BrixBot's comprehensive terms of service covering AI chatbot services, user responsibilities, payment terms, and legal obligations. Important disclaimers about AI technology."
        isIndexable={true}
      />
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl mb-8">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive legal terms governing your use of BrixBot's AI chatbot services. Please read carefully - these terms include important disclaimers about AI technology limitations and your responsibilities.
            </p>
            <div className="mt-6 text-sm text-gray-500">
              Last updated: August 12, 2025 | Effective: August 12, 2025 | Version 2.0
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl text-white flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-gray-300 whitespace-pre-line leading-relaxed">
                    {section.content}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Additional Critical Terms */}
            <Card className="bg-gradient-to-r from-violet-900/30 to-purple-900/30 border-violet-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Critical Legal Provisions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-6">
                  <div>
                    <h4 className="font-semibold text-white mb-3">AI Content Disclaimer & User Responsibility</h4>
                    <p className="mb-2">
                      <strong className="text-red-400">CRITICAL WARNING:</strong> AI-generated responses may be incorrect, harmful, or inappropriate. You are solely responsible for:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Reviewing and validating all AI responses before use</li>
                      <li>Ensuring AI responses comply with applicable laws and regulations</li>
                      <li>Any consequences arising from AI-generated content</li>
                      <li>Monitoring chatbot interactions for inappropriate content</li>
                      <li>Implementing appropriate safeguards and oversight</li>
                    </ul>
                    <p className="mt-2 font-semibold text-red-300">
                      WE DISCLAIM ALL RESPONSIBILITY FOR AI-GENERATED CONTENT AND ITS CONSEQUENCES.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-3">Intellectual Property & Copyright</h4>
                    <p className="mb-2">You warrant and guarantee that:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>You own or have proper licensing for ALL data provided to us</li>
                      <li>All content, images, text, and materials are free from copyright infringement</li>
                      <li>You have obtained all necessary permissions for third-party content</li>
                      <li>Your website content is original or properly licensed</li>
                      <li>You will indemnify us against ALL intellectual property claims</li>
                    </ul>
                    <p className="mt-2 font-semibold text-orange-300">
                      Copyright violations may result in immediate service termination and legal action.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-3">Data Processing & Privacy Compliance</h4>
                    <p className="mb-2">You acknowledge and agree that:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>You are the data controller responsible for GDPR/privacy law compliance</li>
                      <li>You must obtain necessary consents for AI processing of personal data</li>
                      <li>You are liable for any privacy law violations related to your data</li>
                      <li>We process data as your processor under applicable privacy laws</li>
                      <li>Data breach notifications are your responsibility to affected individuals</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Service Level Agreement & Uptime</h4>
                    <p className="mb-2">Service availability and limitations:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>We target 99.5% uptime but provide NO GUARANTEE of continuous availability</li>
                      <li>Scheduled maintenance may occur with 24-hour notice</li>
                      <li>Emergency maintenance may occur without notice</li>
                      <li>Force majeure events excuse performance obligations</li>
                      <li>No compensation for downtime unless exceeding 72 consecutive hours</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-3">Termination & Data Handling</h4>
                    <p className="mb-2">Upon service termination:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Either party may terminate with 30 days written notice</li>
                      <li>We may terminate immediately for Terms violations</li>
                      <li>You receive data export within 30 days of termination</li>
                      <li>We may delete all data 90 days after termination</li>
                      <li>Outstanding fees remain due after termination</li>
                      <li>Confidentiality obligations survive termination indefinitely</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-3">Modifications & Updates</h4>
                    <p className="mb-2">Terms modifications:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>We may modify these Terms at any time with notice</li>
                      <li>Significant changes require 30 days email notice</li>
                      <li>Continued use constitutes acceptance of modified Terms</li>
                      <li>You may terminate service if you disagree with modifications</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-3">Governing Law & Dispute Resolution</h4>
                    <p className="mb-2">Legal framework:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>These Terms are governed by the laws of Slovakia</li>
                      <li>Exclusive jurisdiction: Courts of Žilina, Slovakia</li>
                      <li>Disputes resolved through binding arbitration when possible</li>
                      <li>Each party bears its own legal costs unless otherwise awarded</li>
                      <li>Class action lawsuits are waived to the extent permitted by law</li>
                    </ul>
                  </div>

                  <div className="border-t border-gray-600 pt-4 mt-6">
                    <h4 className="font-semibold text-red-400 mb-3">FINAL ACKNOWLEDGMENT</h4>
                    <p className="text-sm font-medium">
                      BY USING OUR SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE LEGALLY BOUND BY ALL TERMS AND CONDITIONS SET FORTH HEREIN. 
                      YOU FURTHER ACKNOWLEDGE THAT AI TECHNOLOGY CARRIES INHERENT RISKS AND LIMITATIONS, AND YOU ASSUME ALL RESPONSIBILITY FOR ITS USE AND CONSEQUENCES.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legal Contact & Notices */}
            <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Legal Notices & Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Legal Entity & Registration</h4>
                    <p>
                      BrixBot is operated by [Legal Entity Name], registered in Slovakia.<br/>
                      Company Registration Number: [To be provided]<br/>
                      VAT Number: [To be provided]<br/>
                      Registered Address: [To be provided]
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-2">Contact Information</h4>
                    <p>
                      For legal matters, Terms questions, or compliance issues:<br/>
                      <strong>Email:</strong> <span className="text-violet-400">support@brixbot.ai</span><br/>
                      <strong>General Support:</strong> <span className="text-violet-400">support@brixbot.ai</span><br/>
                      <strong>Phone:</strong> <span className="text-violet-400">+421915721942</span><br/>
                      <strong>Business Hours:</strong> Monday-Friday, 9:00-17:00 CET
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-2">Legal Notices & Service of Process</h4>
                    <p>
                      All legal notices, including subpoenas, court orders, and legal process, must be served in writing to:<br/>
                      <strong>Legal Department - BrixBot</strong><br/>
                      [Physical Address to be provided]<br/>
                      Or via certified email to: <span className="text-violet-400">support@brixbot.ai</span>
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-white mb-2">Response Times & Support</h4>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>General inquiries: Response within 24-48 hours</li>
                      <li>Legal matters: Response within 5 business days</li>
                      <li>Emergency technical issues: Response within 4 hours</li>
                      <li>Terms clarification: Response within 2 business days</li>
                    </ul>
                  </div>
                  
                  <div className="border-t border-gray-600 pt-4">
                    <p className="text-sm text-gray-400">
                      <strong>Document Version:</strong> 2.0<br/>
                      <strong>Last Updated:</strong> August 12, 2025<br/>
                      <strong>Effective Date:</strong> August 12, 2025<br/>
                      <strong>Next Review:</strong> December 2025
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;